import * as XLSX from 'xlsx';
import funds from '../../../../services/FundService';

export interface IImportedRow
{
    fecha: string;
    isin: string;
    weight: number;
    rowIndex: number;
    available?: boolean;
}
export interface IDetectedColumns
{
    dateColumn: number;
    isinColumn: number;
    weightColumn: number;
    startRow: number;
}
export interface IProcessFileResult
{
    processedData: IImportedRow[];
    detectedColumns: IDetectedColumns;
    errors: string[];
    warnings: string[];
}
export interface ITemplateRow
{
    fecha: string;
    isin: string;
    peso: number;
}

// Detectar automáticamente las columnas Fecha, ISIN y Peso
export const detectColumns = (data: any[][]): IDetectedColumns | null =>
{
    if(!data || data.length === 0)
    {
        return null;
    }

    // Buscar en las primeras 10 filas para encontrar patrones
    const maxRowsToCheck = Math.min(10, data.length);

    for(let startRow = 0; startRow < maxRowsToCheck; startRow++)
    {
        const row = data[startRow];
        if(!row || row.length < 3) continue;

        // Buscar tres columnas consecutivas que contengan Fecha, ISIN y peso
        for(let col = 0; col < row.length - 2; col++)
        {
            const cell1 = String(row[col] || '').trim();
            const cell2 = String(row[col + 1] || '').trim().toUpperCase();
            const cell3 = String(row[col + 2] || '').trim();

            // Verificar si la primera columna parece una fecha
            const isDatePattern = /^\d{4}-\d{2}-\d{2}$/.test(cell1) ||
                /^\d{2}\/\d{2}\/\d{4}$/.test(cell1) ||
                /^\d{2}-\d{2}-\d{4}$/.test(cell1) ||
                !isNaN(Date.parse(cell1));

            // Verificar si la segunda columna parece un ISIN
            const isIsinPattern = /^[A-Z]{2}[A-Z0-9]{9}[0-9]$/.test(cell2) || (cell2.length >= 10 && /^[A-Z0-9]+$/.test(cell2));

            // Verificar si la tercera columna parece un porcentaje
            const weightValue = parseFloat(cell3.replace(',', '.').replace('%', ''));
            const isWeightPattern = !isNaN(weightValue) && weightValue > 0 && weightValue <= 100;

            if(isDatePattern && isIsinPattern && isWeightPattern)
            {
                return{
                    dateColumn: col,
                    isinColumn: col + 1,
                    weightColumn: col + 2,
                    startRow: startRow
                };
            }
        }
    }

    return null;
};

// Generar datos de ejemplo para las plantillas
export const generateTemplateData = (): ITemplateRow[] =>
{
    const templateData: ITemplateRow[] =
    [
        { fecha: '2024-01-15', isin: 'LU1600598855', peso: 25.5 },
        { fecha: '2024-01-15', isin: 'LU1850283059', peso: 30.0 },
        { fecha: '2024-01-15', isin: 'LU1379136820', peso: 22.3 },
        { fecha: '2024-01-15', isin: 'IE00B4L5Y983', peso: 22.2 },

        // Segunda fecha - suma 100%
        { fecha: '2024-02-21', isin: 'LU2075344718', peso: 20.0 },
        { fecha: '2024-02-21', isin: 'LU1379136820', peso: 25.5 },
        { fecha: '2024-02-21', isin: 'LU1931527326', peso: 10.0 },
        { fecha: '2024-02-21', isin: 'LU1850282754', peso: 25.0 },
        { fecha: '2024-02-21', isin: 'IE00B4L5Y983', peso: 19.5 },

        // Tercera fecha - suma 100%
        { fecha: '2024-03-06', isin: 'LU1379136820', peso: 30.0 },
        { fecha: '2024-03-06', isin: 'LU1850283059', peso: 25.0 },
        { fecha: '2024-03-06', isin: 'LU0274208692', peso: 28.0 },
        { fecha: '2024-03-06', isin: 'LU1850282754', peso: 17.0 },
    ];

    return templateData;
};

// Función alternativa para forzar descarga usando navigator.msSaveBlob (IE/Edge) o FileSaver API
const forceDownload = (blob: Blob, filename: string): boolean =>
{
    try
    {
        // Para navegadores IE/Edge
        if('msSaveOrOpenBlob' in navigator)
        {
            (navigator as any).msSaveOrOpenBlob(blob, filename);
            console.log('forceDownload: Usado msSaveOrOpenBlob');
            return true;
        }

        // Para Safari y otros navegadores que no soportan download attribute
        const reader = new FileReader();
        reader.onload = function ()
        {
            const dataUrl = reader.result as string;
            const link = document.createElement('a');
            link.href = dataUrl;
            link.download = filename;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            console.log('forceDownload: Usado FileReader + data URL');
        };
        reader.readAsDataURL(blob);
        return true;
    }
    catch(error)
    {
        console.error('forceDownload: Error:', error);
        return false;
    }
};

// Procesar datos desde las filas detectadas
export const processDataRows = (jsonData: any[][], detected: IDetectedColumns): IProcessFileResult =>
{
    const processedData: IImportedRow[] = [];
    const newErrors: string[] = [];
    const newWarnings: string[] = [];

    for(let i = detected.startRow; i < jsonData.length; i++)
    {
        const row = jsonData[i];
        if(!row || row.length <= Math.max(detected.dateColumn, detected.isinColumn, detected.weightColumn)) continue;

        const dateValue = String(row[detected.dateColumn] || '').trim();
        const isinValue = String(row[detected.isinColumn] || '').trim().toUpperCase();
        const weightValue = String(row[detected.weightColumn] || '').trim();

        // Saltar filas vacías
        if(!dateValue && !isinValue && !weightValue) continue;

        // Validar fecha
        if(!dateValue)
        {
            newErrors.push(`Fila ${i + 1}: Fecha vacía`);
            continue;
        }

        // Validar formato de fecha
        if(isNaN(Date.parse(dateValue)))
        {
            newErrors.push(`Fila ${i + 1}: Fecha "${dateValue}" no es válida`);
            continue;
        }

        // Validar ISIN
        if(!isinValue)
        {
            newErrors.push(`Fila ${i + 1}: ISIN vacío`);
            continue;
        }

        if(isinValue.length < 10)
        {
            newWarnings.push(`Fila ${i + 1}: ISIN "${isinValue}" parece ser demasiado corto`);
        }

        // Procesar peso
        const cleanWeight = weightValue.replace(',', '.').replace('%', '');
        const weight = parseFloat(cleanWeight);

        if(isNaN(weight))
        {
            newErrors.push(`Fila ${i + 1}: Peso "${weightValue}" no es un número válido`);
            continue;
        }

        if(weight <= 0.01 || weight > 100)
        {
            newErrors.push(`Fila ${i + 1}: Peso ${weight}% debe estar entre 0,01% y 100%`);
            continue;
        }

        processedData.push(
        {
            fecha: dateValue,
            isin: isinValue,
            weight: weight,
            rowIndex: i + 1
        });
    }

    // Validar que la suma sea el 100% por fecha
    const tolerance = 0.01; // Tolerancia de 0.01%
    const weightsByDate = new Map<string, number>();

    processedData.forEach(item =>
    {
        const currentWeight = weightsByDate.get(item.fecha) || 0;
        weightsByDate.set(item.fecha, currentWeight + item.weight);
    });

    weightsByDate.forEach((totalWeight, fecha) =>
    {
        if(Math.abs(totalWeight - 100) > tolerance)
        {
            newErrors.push(`Fecha ${fecha}: La suma de pesos es ${totalWeight.toFixed(2)}% (debe ser 100%)`);
        }
    });

    // Verificar ISINs duplicados POR FECHA (mismo ISIN no puede repetirse en la misma fecha)
    const isinsByDate = new Map<string, Set<string>>();
    
    processedData.forEach(item =>
    {
        if(!isinsByDate.has(item.fecha))
        {
            isinsByDate.set(item.fecha, new Set<string>());
        }

        const isinsForDate = isinsByDate.get(item.fecha)!;
        if(isinsForDate.has(item.isin))
        {
            newErrors.push(`Fila ${item.rowIndex}: ISIN duplicado "${item.isin}" en la fecha ${item.fecha}`);
        }
        else
        {
            isinsForDate.add(item.isin);
        }
    });

    return{
        processedData,
        detectedColumns: detected,
        errors: newErrors,
        warnings: newWarnings
    };
};

// Procesar archivo Excel o CSV
export const processFile = async (file: File, fileType: 'excel' | 'csv'): Promise<IProcessFileResult> =>
{
    return new Promise(async (resolve, reject) =>
    {
        const reader = new FileReader();

        reader.onload = async (e) =>
        {
            try
            {
                const data = e.target?.result;
                let workbook: XLSX.WorkBook;

                if(fileType === 'excel')
                {
                    workbook = XLSX.read(data, { type: 'binary' });
                }
                else
                {
                    // Para CSV, crear un workbook desde el texto
                    const csvText = new TextDecoder().decode(data as ArrayBuffer);
                    workbook = XLSX.read(csvText, { type: 'string' });
                }

                // Obtener la primera hoja
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];

                // Convertir a array de arrays
                const jsonData = XLSX.utils.sheet_to_json(worksheet,
                {
                    header: 1,
                    defval: '',
                    raw: false
                }) as any[][];

                // Detectar columnas automáticamente
                const detected = detectColumns(jsonData);
                if(!detected)
                {
                    reject(new Error('No se pudieron detectar las columnas ISIN y Peso. Verifica que el archivo contenga datos válidos.'));
                    return;
                }

                // Procesar datos desde la fila detectada
                const result = processDataRows(jsonData, detected);

                if(result.errors.length > 0)
                {
                    reject(new Error(`Se encontraron ${result.errors.length} errores en el archivo`));
                    return;
                }

                // Verificar disponibilidad de fondos
                try
                {
                    const isins = result.processedData.map(row => row.isin);
                    const availabilityMap = await checkFundsAvailability(isins);
                    
                    // Asignar disponibilidad a cada fila
                    result.processedData.forEach(row =>
                    {
                        row.available = availabilityMap.get(row.isin) ?? false;
                    });
                }
                catch(availabilityError)
                {
                    console.error('Error verificando disponibilidad de fondos:', availabilityError);
                    
                    // En caso de error, marcar todos como no disponibles
                    result.processedData.forEach(row =>
                    {
                        row.available = false;
                    });
                }

                resolve(result);
            }
            catch(error)
            {
                reject(new Error(`Error procesando archivo: ${error instanceof Error ? error.message : 'Error desconocido'}`));
            }
        };

        reader.onerror = () =>
        {
            reject(new Error('Error leyendo el archivo'));
        };

        if(fileType === 'excel')
        {
            reader.readAsBinaryString(file);
        }
        else
        {
            reader.readAsArrayBuffer(file);
        }
    });
};

// Descargar plantilla Excel
export const downloadExcelTemplate = (): void =>
{
    try
    {
        const templateData = generateTemplateData();
        
        // Crear workbook
        const wb = XLSX.utils.book_new();
        
        // Convertir datos a formato de hoja de cálculo
        const wsData =
        [
            ['Fecha', 'ISIN', 'Peso'], // Headers
            ...templateData.map(row => [row.fecha, row.isin, row.peso])
        ];
        
        // Crear worksheet
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        
        // Agregar worksheet al workbook
        XLSX.utils.book_append_sheet(wb, ws, 'Plantilla de Importación');
        
        // Usar método directo con blob (más confiable)
        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        
        // Crear link de descarga
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'plantilla_composicion_cartera_modelo.xlsx';
        link.style.display = 'none';
        
        // Agregar al DOM, hacer clic y limpiar
        document.body.appendChild(link);
        
        // Intentar métodos de descarga en orden, deteniéndose en el primero que funcione
        let downloadSuccess = false;
        
        // Método 1: Click directo + Dispatch event (doble método por compatibilidad)
        if(!downloadSuccess)
        {
            try
            {
                link.click();
                console.log('downloadExcelTemplate: Click directo ejecutado, aplicando dispatch event adicional');

                // Método adicional inmediatamente después
                const clickEvent = new MouseEvent('click',
                {
                    view: window,
                    bubbles: true,
                    cancelable: false
                });
                link.dispatchEvent(clickEvent);
                downloadSuccess = true;
                console.log('downloadExcelTemplate: Descarga iniciada con métodos combinados');
            }
            catch(clickError)
            {
                console.warn('downloadExcelTemplate: Métodos combinados fallaron, probando window.open');
                downloadSuccess = false;
            }
        }

        if(!downloadSuccess)
        {
            try
            {
                const newWindow = window.open(url, '_blank');
                if(newWindow)
                {
                    newWindow.document.title = 'plantilla_composicion_cartera.xlsx';
                    downloadSuccess = true;
                    console.log('downloadExcelTemplate: Descarga iniciada con window.open');
                }
                else
                {
                    console.warn('downloadExcelTemplate: window.open bloqueado, probando método final');
                    downloadSuccess = false;
                }
            }
            catch(windowError)
            {
                console.warn('downloadExcelTemplate: window.open falló, probando método final');
                downloadSuccess = false;
            }
        }

        // Método 3: Función alternativa - solo como último recurso
        if(!downloadSuccess)
        {
            try
            {
                const forceResult = forceDownload(blob, 'plantilla_composicion_cartera.xlsx');
                if(forceResult)
                {
                    downloadSuccess = true;
                    console.log('downloadExcelTemplate: Descarga iniciada con forceDownload');
                }
                else
                {
                    console.error('downloadExcelTemplate: Todos los métodos de descarga fallaron');
                }
            }
            catch(forceError)
            {
                console.error('downloadExcelTemplate: Todos los métodos de descarga fallaron');
            }
        }
        
        // Limpiar después de un pequeño delay para asegurar que la descarga inicie
        setTimeout(() =>
        {
            if (document.body.contains(link))
            {
                document.body.removeChild(link);
            }
            URL.revokeObjectURL(url);
        }, 1000);
    }
    catch(error)
    {
        console.error('downloadExcelTemplate: Error completo:', error);
        throw error;
    }
};

// Descargar plantilla CSV
export const downloadCSVTemplate = (): void =>
{
    try
    {
        const templateData = generateTemplateData();

        // Crear contenido CSV
        const headers = ['Fecha', 'ISIN', 'Peso'];
        const csvContent =
        [
            headers.join(','),
            ...templateData.map(row => `${row.fecha},${row.isin},${row.peso}`)
        ].join('\n');

        // Crear blob y descargar
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = 'plantilla_composicion_cartera_modelo.csv';
        link.style.display = 'none';

        document.body.appendChild(link);

        // Intentar métodos de descarga en orden, deteniéndose en el primero que funcione
        let downloadSuccess = false;

        // Método 1: Click directo
        if(!downloadSuccess)
        {
            try
            {
                link.click();
                // Esperar un momento para verificar si realmente funcionó
                setTimeout(() =>
                {
                    console.log('downloadCSVTemplate: Click ejecutado, verificando...');
                }, 100);

                // No asumir éxito inmediatamente, continuar con métodos alternativos
                console.log('downloadCSVTemplate: Click directo ejecutado, probando método adicional por seguridad');
            }
            catch(clickError)
            {
                console.warn('downloadCSVTemplate: Click directo falló con error:', clickError);
            }
        }

        // Método 1b: Dispatch event (siempre ejecutar como backup)
        if(!downloadSuccess)
        {
            try
            {
                const clickEvent = new MouseEvent('click',
                {
                    view: window,
                    bubbles: true,
                    cancelable: false
                });
                link.dispatchEvent(clickEvent);
                downloadSuccess = true;
                console.log('downloadCSVTemplate: Descarga iniciada con dispatch event');
            }
            catch(dispatchError)
            {
                console.warn('downloadCSVTemplate: Dispatch event falló, probando window.open');
                downloadSuccess = false;
            }
        }

        // Método 2: window.open - si dispatch event falló
        if(!downloadSuccess)
        {
            try
            {
                const newWindow = window.open(url, '_blank');
                if(newWindow)
                {
                    newWindow.document.title = 'plantilla_composicion_cartera.csv';
                    downloadSuccess = true;
                    console.log('downloadCSVTemplate: Descarga iniciada con window.open');
                }
                else
                {
                    console.warn('downloadCSVTemplate: window.open bloqueado, probando método final');
                    downloadSuccess = false;
                }
            }
            catch(windowError)
            {
                console.warn('downloadCSVTemplate: window.open falló, probando método final');
                downloadSuccess = false;
            }
        }

        // Método 3: Función alternativa - solo como último recurso
        if(!downloadSuccess)
        {
            try
            {
                const forceResult = forceDownload(blob, 'plantilla_composicion_cartera.csv');
                if(forceResult)
                {
                    downloadSuccess = true;
                    console.log('downloadCSVTemplate: Descarga iniciada con forceDownload');
                }
                else
                {
                    console.error('downloadCSVTemplate: Todos los métodos de descarga fallaron');
                }
            }
            catch(forceError)
            {
                console.error('downloadCSVTemplate: Todos los métodos de descarga fallaron');
            }
        }

        // Limpiar después de un pequeño delay
        setTimeout(() =>
        {
            if (document.body.contains(link))
            {
                document.body.removeChild(link);
            }
            URL.revokeObjectURL(url);
        }, 1000);
    }
    catch(error)
    {
        console.error('downloadCSVTemplate: Error completo:', error);
        throw error;
    }
};


/**
 * Verifica la disponibilidad de los fondos usando getAvailableFunds
 * @param isins - Array de ISINs únicos a verificar
 * @returns Promise<Map<string, boolean>> - Mapa de ISIN -> disponibilidad
 */
export const checkFundsAvailability = async (isins: string[]): Promise<Map<string, boolean>> =>
{
    const availabilityMap = new Map<string, boolean>();
    
    try
    {
        // Obtener ISINs únicos
        const uniqueIsins = Array.from(new Set(isins));
        
        if(uniqueIsins.length === 0)
        {
            return availabilityMap;
        }

        // Llamar al servicio para obtener fondos disponibles
        const availableFunds = await funds.getAvailableFundsByIsins(uniqueIsins);
        
        // Crear un mapa de ISINs disponibles para búsqueda rápida
        const availableIsins = new Set(availableFunds.map(fund => fund.isin));
        
        // Mapear cada ISIN a su disponibilidad
        for(const isin of uniqueIsins)
        {
            availabilityMap.set(isin, availableIsins.has(isin));
        }
    }
    catch(error)
    {
        console.error('Error verificando disponibilidad de fondos:', error);
        
        // En caso de error, marcar todos como no disponibles (false)
        for(const isin of isins)
        {
            availabilityMap.set(isin, false);
        }
    }
    
    return availabilityMap;
};

/**
 * Convierte cualquier formato de fecha a dd/MM/yyyy para visualización
 * @param dateValue - Valor de fecha en cualquier formato
 * @returns Fecha formateada como dd/MM/yyyy o string original si no es válida
 */
export const formatDateForDisplay = (dateValue: any): string =>
{
    if(!dateValue)
    {
        return '';
    }

    // Convertir a string si no lo es
    const dateStr = String(dateValue).trim();

    if(!dateStr)
    {
        return '';
    }

    try
    {
        let date: Date;

        // Probamos con diferentes formatos de fecha
        if(/^\d{4}-\d{2}-\d{2}$/.test(dateStr))
        {
            // Formato YYYY-MM-DD
            date = new Date(dateStr);
        }
        else if(/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr))
        {
            // Formato DD/MM/YYYY
            const [day, month, year] = dateStr.split('/');
            date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        }
        else if(/^\d{2}-\d{2}-\d{4}$/.test(dateStr))
        {
            // Formato DD-MM-YYYY
            const [day, month, year] = dateStr.split('-');
            date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        }
        else if(/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateStr))
        {
            // Formato D/M/YYYY o DD/M/YYYY o D/MM/YYYY
            const [day, month, year] = dateStr.split('/');
            date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        }
        else
        {
            // Intentar parsear como fecha general
            date = new Date(dateStr);
        }

        // Verificar si la fecha es válida
        if(isNaN(date.getTime()))
        {
            return dateStr;
        }

        // Formatear a dd/MM/yyyy
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();

        return `${day}/${month}/${year}`;
    }
    catch(error)
    {
        console.warn('Error formateando fecha:', dateValue, error);
        return dateStr;
    }
};